import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { Search, Layers, Repeat, Boxes } from 'lucide-react';
// The four core points, drawn from the "AI Work Stack" article: the pain isn't
// missing tools, it's scattered data across too many of them — and the fix is
// one layer over the whole stack, not another tool. Rendered as a clean cell
// grid with hairline dividers and bare orange outline icons.
const CAPABILITIES = [{
  id: 'tools-not-answers',
  title: 'You have the tools, not the answers',
  desc: 'Most teams aren’t short on software — they’re drowning in it. Fourteen dashboards, and not one answers your actual question.',
  icon: Search
}, {
  id: 'not-your-stack',
  title: 'The problem isn’t your stack',
  desc: 'It’s the gaps between it. More tools don’t fix scattered data — they scatter it further. It’s an architecture problem, not a tool problem.',
  icon: Layers
}, {
  id: 'context-switch-tax',
  title: 'Stop paying the context-switch tax',
  desc: 'The average worker jumps between 11 apps a day, losing focus with every switch. Giga works across all of them so you don’t have to.',
  icon: Repeat
}, {
  id: 'one-layer',
  title: 'One layer over everything',
  desc: 'Giga doesn’t add another tab. It ties your whole stack together so context — and answers — flow freely.',
  icon: Boxes
}];
export function CapabilityGrid() {
  return <section className="w-full bg-[#FAF8F4] py-24 md:py-32" data-id="element-576">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-577">
        {/* Header */}
        <div className="text-center max-w-4xl mx-auto mb-16 md:mb-20" data-id="element-578">
          <h2 className="text-3xl md:text-5xl font-title font-medium text-[#2B211A] tracking-tight leading-[1.1] md:whitespace-nowrap" data-id="element-579">
            Built to do the work — not just talk about it.
          </h2>
        </div>

        {/* Cell grid with ONLY the inner hairline dividers — no outer frame.
           Borders are applied per-cell so the outer top/bottom/left/right
           edges stay open and only the inner cross (and mobile stacked
           dividers) remain. */}
        <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-2" data-id="element-580">
          {CAPABILITIES.map((cap, index) => {
          const Icon = cap.icon;
          // Inner-only dividers:
          // - bottom border on the top row (index 0,1) draws the horizontal
          //   inner line on desktop; on mobile, every item but the last gets
          //   a bottom border so stacked items stay separated.
          // - right border on the left column (index 0,2) draws the vertical
          //   inner line on desktop only.
          const dividerClasses = [index < 3 ? 'border-b border-neutral-200/70' : '', index >= 2 ? 'sm:border-b-0' : '', index % 2 === 0 ? 'sm:border-r sm:border-neutral-200/70' : ''].filter(Boolean).join(' ');
          return <motion.div key={cap.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.5,
            delay: index % 2 * 0.1
          }} className={`flex flex-col items-start text-left ${dividerClasses} px-8 py-10 md:px-12 md:py-14`} data-id="element-581">
                <Icon strokeWidth={1.75} className="w-9 h-9 text-[#EA580C] mb-8" data-id="element-582" />
                <h3 className="text-xl md:text-2xl font-bold mb-3 text-[#2B211A] tracking-tight" data-id="element-583">
                  {cap.title}
                </h3>
                <p className="leading-relaxed text-[15px] md:text-base text-neutral-500 max-w-sm" data-id="element-584">
                  {cap.desc}
                </p>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}